import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  ComplianceDocumentDefinitionRequest,
  ComplianceDocumentKind,
  ComplianceDocumentType,
  complianceDocumentDefinitionsApi,
} from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { ComplianceDocumentDefinitionForm } from './ComplianceDocumentDefinitionForm';

interface Props extends SharedDisclosureProps {
  folderId: number;
  onCreated: () => void;
}

const defaultValues: ComplianceDocumentDefinitionRequest = {
  name: '',
  description: '',
  kind: ComplianceDocumentKind.Certificate,
  type: ComplianceDocumentType.Scoped,
  logoFileId: null,
};

export function CreateComplianceDocumentDefinition({
  folderId,
  isOpen,
  onClose,
  onCreated,
}: Props) {
  return (
    <Modal
      variant="entityDirectory"
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      {isOpen && (
        <Inner folderId={folderId} onClose={onClose} onCreated={onCreated} />
      )}
    </Modal>
  );
}

function Inner({ folderId, onClose, onCreated }: Omit<Props, 'isOpen'>) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    request: create,
    loading,
    error,
  } = useApiRequest(complianceDocumentDefinitionsApi.create);

  const submit = async (request: ComplianceDocumentDefinitionRequest) => {
    const response = await create(request);
    if (response.hasError) return;

    toast({
      title: t('general.created'),
      status: 'success',
      isClosable: true,
    });
    onCreated();
    onClose();
  };

  return (
    <ModalContent>
      <ModalCloseButton />
      <ModalHeader>
        {t('complianceDocumentDefinitions.createDefinition')}
        <Text
          mt={1}
          textTransform="none"
          variant="entity-directory-description"
        >
          {t('complianceDocumentDefinitions.createDescription')}
        </Text>
      </ModalHeader>
      <ComplianceDocumentDefinitionForm
        defaultValues={defaultValues}
        error={error}
        folderId={folderId}
        initialLogo={null}
        isSubmitting={loading}
        onClose={onClose}
        onSubmit={submit}
        submitLabel={t('complianceDocumentDefinitions.createDefinition')}
      />
    </ModalContent>
  );
}
